import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getListSecretsQueryKey,
  useAddSecretVersion2,
  useCreateSecret2,
  useDeleteSecret2,
} from '@/generated/hooks/gringotts/v1/services/secret_service';
import { captureException } from '@/modules/sentry';

import {
  markManagedOAuthReturn,
  unmarkManagedOAuthReturn,
} from '../oauth-resume';

/** Arguments for {@link useCreateOAuthCredential}'s `createOAuthCredential`. */
export interface ICreateOAuthCredentialArgs {
  displayName: string;
  secretTypeName: string;
  oauthAppId: string;
  /** Values for the app's arguments that have no constant. */
  arguments: Record<string, string>;
}

/** The new credential, plus the consent URL when the user must sign in. */
export interface ICreateOAuthCredentialResult {
  credentialId: string;
  /** Present for authorization-code apps; absent once a token was fetched. */
  authorizeUrl?: string;
}

/**
 * Creates a workspace OAuth credential bound to an OAuth app and starts its
 * grant. Authorization-code apps return a consent URL (the credential stays
 * pending until the user signs in); client-credentials apps fetch a token
 * immediately. If starting the grant fails, the new credential is deleted.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @returns `createOAuthCredential` and its pending state.
 */
export function useCreateOAuthCredential(
  organizationId: string,
  workspaceId: string,
) {
  const queryClient = useQueryClient();
  const createSecret = useCreateSecret2();
  const addVersion = useAddSecretVersion2();
  const deleteSecret = useDeleteSecret2();

  const createOAuthCredential = useCallback(
    async ({
      displayName,
      secretTypeName,
      oauthAppId,
      arguments: oauthArguments,
    }: ICreateOAuthCredentialArgs): Promise<ICreateOAuthCredentialResult> => {
      const created = await createSecret.mutateAsync({
        organizationId,
        workspaceId,
        data: {
          secret: {
            display_name: displayName,
            secret_type: secretTypeName,
            oauth_app_id: oauthAppId,
          },
        },
      });
      const credentialId = created.data.id ?? '';

      // The request captures this page as the return address.
      markManagedOAuthReturn();
      try {
        const version = await addVersion.mutateAsync({
          organizationId,
          workspaceId,
          id: credentialId,
          data: { oauth_payload: { arguments: oauthArguments } },
        });
        if (!version.data.authorize_url) unmarkManagedOAuthReturn();
        return {
          credentialId,
          authorizeUrl: version.data.authorize_url || undefined,
        };
      } catch (error) {
        unmarkManagedOAuthReturn();
        await deleteSecret
          .mutateAsync({ organizationId, workspaceId, id: credentialId })
          .catch(cleanupError =>
            captureException(cleanupError, {
              tags: {
                module: 'credentials',
                action: 'createOAuthCredentialCleanup',
              },
            }),
          );
        throw error;
      } finally {
        void queryClient.invalidateQueries({
          queryKey: getListSecretsQueryKey(organizationId),
        });
      }
    },
    [
      organizationId,
      workspaceId,
      queryClient,
      createSecret,
      addVersion,
      deleteSecret,
    ],
  );

  return {
    createOAuthCredential,
    isPending: createSecret.isPending || addVersion.isPending,
  };
}
